import {
  CURRENT_PRIVACY_EFFECTIVE_DATE_V1,
  CURRENT_PRIVACY_VERSION_V1,
  CURRENT_TERMS_EFFECTIVE_DATE_V1,
  CURRENT_TERMS_VERSION_V1,
  PREVIEW_PRIVACY_EFFECTIVE_DATE_V1,
  PREVIEW_PRIVACY_VERSION_V1,
  PREVIEW_TERMS_EFFECTIVE_DATE_V1,
  PREVIEW_TERMS_VERSION_V1,
} from "./legal-document-versions.generated.js";

export {
  CURRENT_PRIVACY_EFFECTIVE_DATE_V1,
  CURRENT_PRIVACY_VERSION_V1,
  CURRENT_TERMS_EFFECTIVE_DATE_V1,
  CURRENT_TERMS_VERSION_V1,
};

export const LEGAL_DOCUMENTS_SCHEMA_VERSION_V1 = 1;
export const LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2 = 2;

export const CURRENT_LEGAL_PUBLICATION = Object.freeze({
  termsVersion: CURRENT_TERMS_VERSION_V1,
  termsEffectiveDate: CURRENT_TERMS_EFFECTIVE_DATE_V1,
  privacyVersion: CURRENT_PRIVACY_VERSION_V1,
  privacyEffectiveDate: CURRENT_PRIVACY_EFFECTIVE_DATE_V1,
});

export const PREVIEW_LEGAL_PUBLICATION = Object.freeze({
  termsVersion: PREVIEW_TERMS_VERSION_V1,
  termsEffectiveDate: PREVIEW_TERMS_EFFECTIVE_DATE_V1,
  privacyVersion: PREVIEW_PRIVACY_VERSION_V1,
  privacyEffectiveDate: PREVIEW_PRIVACY_EFFECTIVE_DATE_V1,
});

export function allowsDraftLegalAcceptance(environment = globalThis.window?.__fishystuffRuntimeConfig?.build?.deploymentEnvironment ?? "production") {
  if (["local", "development", "preview", "beta"].includes(environment)) return true;
  if (["", "unknown", "production"].includes(environment)) return false;
  throw new TypeError("Legal publication environment is unsupported.");
}

export function requiredLegalPublication(environment) {
  return allowsDraftLegalAcceptance(environment) ? PREVIEW_LEGAL_PUBLICATION : CURRENT_LEGAL_PUBLICATION;
}

export class LegalDocumentsUnavailableError extends Error {
  constructor() {
    super("Terms acceptance is unavailable in this environment because no required Terms have been published.");
    this.name = "LegalDocumentsUnavailableError";
  }
}

export function hasRequiredLegalDocuments(publication = requiredLegalPublication()) {
  return publication != null
    && [publication.termsVersion, publication.privacyVersion].every((value) =>
      typeof value === "string" && /^sha256-[A-Za-z0-9_-]{43}$/.test(value))
    && [publication.termsEffectiveDate, publication.privacyEffectiveDate].every((value) =>
      typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value));
}

export function requireLegalDocuments(publication = requiredLegalPublication()) {
  if (!hasRequiredLegalDocuments(publication)) throw new LegalDocumentsUnavailableError();
  return publication;
}

export function canAcceptTermsAtUrl(url, publication = requiredLegalPublication()) {
  if (!hasRequiredLegalDocuments(publication)) return false;
  const query = new URL(url).searchParams;
  const versions = query.getAll("version");
  return !query.has("compare")
    && (versions.length === 0 || (versions.length === 1 && versions[0] === publication.termsVersion));
}

export const LEGAL_ACCEPTANCE_FIELDS = Object.freeze({
  termsAccepted: "fishystuff.legal.termsAccepted",
  termsVersion: "fishystuff.legal.termsVersion",
  locale: "fishystuff.legal.locale",
});

export const REGISTRATION_TRANSIENT_PAYLOAD_FIELD = "transient_payload";
export const LEGAL_ACCEPTANCE_TRANSIENT_KEY = "fishystuffLegalAcceptance";

const LOCALES = new Set(["en-US", "de-DE"]);

function exactValue(submission, name) {
  const values = submission.getAll(name);
  if (values.length !== 1 || typeof values[0] !== "string") {
    throw new TypeError("Registration requires one exact legal acceptance value.");
  }
  return values[0];
}

export function appendCurrentLegalAcceptance(submission, locale, publication = requiredLegalPublication()) {
  if (!(submission instanceof URLSearchParams) || !LOCALES.has(locale)) {
    throw new TypeError("Registration legal acceptance input is invalid.");
  }
  const effective = requireLegalDocuments(publication);
  submission.set(LEGAL_ACCEPTANCE_FIELDS.termsAccepted, "true");
  submission.set(LEGAL_ACCEPTANCE_FIELDS.termsVersion, effective.termsVersion);
  submission.set(LEGAL_ACCEPTANCE_FIELDS.locale, locale);
  return submission;
}

export function prepareRegistrationLegalAcceptance(submission, publication = requiredLegalPublication()) {
  if (!(submission instanceof URLSearchParams)) {
    throw new TypeError("Registration legal acceptance requires URLSearchParams.");
  }
  const effective = requireLegalDocuments(publication);
  if (submission.getAll(REGISTRATION_TRANSIENT_PAYLOAD_FIELD).length !== 0) {
    throw new TypeError("Registration transient payload has an unexpected owner.");
  }
  const command = {
    schemaVersion: LEGAL_ACCEPTANCE_SCHEMA_VERSION_V2,
    termsVersion: exactValue(submission, LEGAL_ACCEPTANCE_FIELDS.termsVersion),
    termsAccepted: exactValue(submission, LEGAL_ACCEPTANCE_FIELDS.termsAccepted) === "true",
    locale: exactValue(submission, LEGAL_ACCEPTANCE_FIELDS.locale),
  };
  if (
    command.termsAccepted !== true
    || command.termsVersion !== effective.termsVersion
    || !LOCALES.has(command.locale)
  ) {
    throw new TypeError("Current Terms must be accepted before registration.");
  }
  const sanitized = new URLSearchParams(submission);
  for (const field of Object.values(LEGAL_ACCEPTANCE_FIELDS)) sanitized.delete(field);
  sanitized.set(
    REGISTRATION_TRANSIENT_PAYLOAD_FIELD,
    JSON.stringify({ [LEGAL_ACCEPTANCE_TRANSIENT_KEY]: command }),
  );
  return sanitized;
}

export function legalDocumentHrefs(locale) {
  if (!LOCALES.has(locale)) throw new TypeError("Legal document locale is unsupported.");
  const prefix = locale === "de-DE" ? "/de-DE" : "";
  return Object.freeze({
    terms: `${prefix}/terms/`,
    privacy: `${prefix}/privacy/`,
  });
}
